// ============================================
// Customers Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { CustomerListParams, CreateCustomerRequest, UpdateCustomerRequest, CustomerStats } from '@conai/shared-types';
import { useCustomersService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { useI18n } from '../i18n';

export function useCustomers(params: CustomerListParams = {}) {
  const customersService = useCustomersService();

  return useQuery({
    queryKey: queryKeys.customers.list(params as Record<string, unknown>),
    queryFn: () => customersService.list(params),
  });
}

export function useCustomersStats() {
  const customersService = useCustomersService();

  return useQuery<CustomerStats>({
    queryKey: [...queryKeys.customers.all, 'stats'] as const,
    queryFn: () => customersService.getStats(),
  });
}

export function useCreateCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (data: CreateCustomerRequest) => customersService.create(data),
    onSuccess: (newCustomer) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerCreated', { name: newCustomer.full_name }));
    },
  });
}

export function useUpdateCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    /**
     * Bug #104: PATCH chứ không PUT.
     *
     * `customersService.update()` gọi PUT — DRF hiểu PUT là "thay TOÀN BỘ bản
     * ghi", nên mọi field không có trong payload đều bị coi là muốn xoá/thiếu.
     * Màn Customers chỉ có 4 ô nhập, nên bật một cái checkbox marketing consent
     * lại kéo theo việc khai lại cả hồ sơ khách — khách nào thiếu dữ liệu
     * (chưa có SĐT/email, chưa có cấp bậc) là 400 ngay.
     *
     * PATCH merge theo key: gửi key nào sửa key đó, còn lại giữ nguyên.
     */
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateCustomerRequest> }) =>
      customersService.patch(id, data),
    onSuccess: (updatedCustomer) => {
      queryClient.setQueryData(queryKeys.customers.detail(updatedCustomer.id), updatedCustomer);
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerUpdated', { name: updatedCustomer.full_name }));
    },
  });
}

/**
 * Gắn tài khoản đăng nhập vào hồ sơ khách (sheet 2 mục #65).
 *
 * 409 nghĩa là hồ sơ ĐÃ liên kết — trạng thái nghiệp vụ, không phải sự cố. Bắt
 * riêng để thông báo đúng việc thay vì đổ ra thông báo lỗi chung, vì người
 * dùng cần biết phải gỡ liên kết cũ trước chứ không phải thử lại.
 */
export function useLinkCustomerUser() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({ customerId, userId }: { customerId: string; userId: string }) =>
      customersService.linkUser(customerId, userId),
    onSuccess: (customer) => {
      queryClient.setQueryData(queryKeys.customers.detail(customer.id), customer);
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerLinked'));
    },
    onError: (err: unknown) => {
      const status = (err as { status?: number; response?: { status?: number } })?.status
        ?? (err as { response?: { status?: number } })?.response?.status;
      toast.error(status === 409 ? t('toastCustomerAlreadyLinked') : t('toastCustomerLinkFailed'));
    },
  });
}

export function useUnlinkCustomerUser() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (customerId: string) => customersService.unlinkUser(customerId),
    onSuccess: (customer) => {
      queryClient.setQueryData(queryKeys.customers.detail(customer.id), customer);
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerUnlinked'));
    },
    onError: () => toast.error(t('toastCustomerUnlinkFailed')),
  });
}

export function useDeleteCustomer() {
  const customersService = useCustomersService();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => customersService.delete(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: queryKeys.customers.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
      toast.success(t('toastCustomerDeleted'));
    },
  });
}
